"use client";

/**
 * Состояние «здоровья данных» для значка в шапке (lib/data-health.ts).
 *
 * Спрашиваем редко: проблемы меняются обновлением данных, то есть раз в сутки,
 * а сервер к тому же держит краткий ответ минуту (readDataHealthCached). Пять
 * минут на видимой вкладке — достаточно, чтобы после ручного обновления значок
 * погас сам, и при этом почти бесплатно.
 *
 * На страницах общего доступа (/shared/…) сессии нет — там не спрашиваем
 * вовсе, иначе гость получал бы редирект на вход.
 */

import { useMemo } from "react";
import useSWR from "swr";
import { usePathname } from "next/navigation";
import { apiFetch } from "@/lib/api/client";
import { withDataScope } from "@/app/context/DataScopeContext";
import { dataHealthSeverity, type DataHealthPayload } from "@/lib/data-health";

const REFRESH_MS = 5 * 60 * 1000;
const EMPTY: DataHealthPayload["issues"] = [];

const fetchHealth = (url: string) => apiFetch<DataHealthPayload>(url);

export function useDataHealth(scopeQuery: string, enabled = true) {
  const pathname = usePathname();
  const isShared = (pathname ?? "").startsWith("/shared/");
  const url = enabled && !isShared ? withDataScope("/api/data-health", scopeQuery) : null;
  const { data, mutate } = useSWR(url, fetchHealth, {
    refreshInterval: REFRESH_MS,
    refreshWhenHidden: false,
    revalidateOnFocus: false,
    dedupingInterval: 60_000,
    keepPreviousData: true,
  });
  const issues = data?.issues ?? EMPTY;
  const severity = useMemo(() => dataHealthSeverity(issues), [issues]);
  return { issues, severity, reload: mutate };
}
